import React, {FC} from "react";
import classNames from "classnames";
import {
    LessonBookingIntentInput,
    LessonBookingPlanApplies,
    LessonBookingPlannerPreviewQuery,
    LessonBookingPlannerRateStatus,
    LessonBookingPlannerUnitStatus,
    LessonPricingMode,
} from "../../../generated/graphql";
import {changed, date, day, eur, signed} from "./plannerFormat";
import css from "./PlannerSummary.less";

// Kurzfassung über der Vorschau „Neu“ (Entscheidung 26.10.2026, Entwurf „Ablauf“): eine Zeile je Schritt mit der Zahl voran,
// eine Linie verbindet die Schritte, weil eins aus dem anderen folgt: Einheiten → Preis → Raten → Guthaben.
// Schreibt der Server kein Geld (applies ≠ Money: Warteliste, individuell, Pauschalpreis, storniert, …), steht nur „bleibt“
// mit dem Grund, auch wenn der Planner Preis und Raten gerechnet hat. Die Guthaben-Zeile gibt es nur mit Guthaben; ab vier
// geänderten Raten steht eine Zeile mit der Summe.

type PlannerData = LessonBookingPlannerPreviewQuery["lessonBookingPlannerPreview"];

export type SummaryTone = "minus" | "plus" | "neutral" | "keep" | "credit" | "open";
export type SummaryRow = {key: string; number: string; text: string; detail?: string; tone: SummaryTone};
export type SummaryInput = {data: PlannerData; intent?: LessonBookingIntentInput; isWaitlist?: boolean};

const MAX_RATE_ROWS = 3;
const tone = (amount: number): SummaryTone => (changed(amount, 0) ? (amount < 0 ? "minus" : "plus") : "neutral");
const list = (items: Array<string>) => (items.length === 2 ? `${items[0]} und ${items[1]}` : items.join(", "));
const joinDetail = (...parts: Array<string | undefined>) => parts.filter(Boolean).join(", ") || undefined;
const count = (n: number, singular: string, plural: string) => (n === 1 ? singular : plural);

const unitsRow = (data: PlannerData): SummaryRow => {
    if (data.regenerated) return {key: "units", number: "offen", text: "Alle Kurseinheiten neu erzeugt, Zuordnung zur Buchung offen", detail: "Bestand bleibt stehen", tone: "open"};
    const removed = data.units.filter((unit) => unit.status === LessonBookingPlannerUnitStatus.Removed).map((unit) => day(unit.lessonDate));
    const added = data.units.filter((unit) => unit.status === LessonBookingPlannerUnitStatus.Added).map((unit) => day(unit.lessonDate));
    const moved = data.units.filter((unit) => unit.movedTo && unit.status !== LessonBookingPlannerUnitStatus.Removed).map((unit) => `${day(unit.lessonDate)} → ${day(unit.movedTo as string)}`);
    const movedDetail = moved.length > 0 ? `${list(moved)} verschoben` : undefined;

    if (removed.length > 0 && added.length > 0) {
        return {
            key: "units",
            number: `−${removed.length} / +${added.length}`,
            text: "Kurseinheiten",
            detail: joinDetail(`${list(removed)} entfallen`, `${list(added)} dazu`, movedDetail),
            tone: "neutral",
        };
    }
    if (removed.length > 0)
        return {
            key: "units",
            number: `−${removed.length}`,
            text: count(removed.length, "Kurseinheit entfällt", "Kurseinheiten entfallen"),
            detail: joinDetail(list(removed), movedDetail),
            tone: "minus",
        };
    if (added.length > 0)
        return {key: "units", number: `+${added.length}`, text: count(added.length, "Kurseinheit dazu", "Kurseinheiten dazu"), detail: joinDetail(list(added), movedDetail), tone: "plus"};
    if (moved.length > 0) return {key: "units", number: String(moved.length), text: count(moved.length, "Kurseinheit verschoben", "Kurseinheiten verschoben"), detail: list(moved), tone: "neutral"};

    return {key: "units", number: "0", text: "Kurseinheiten dieser Buchung unverändert", tone: "keep"};
};

// Warum Preis und Raten bleiben, in der Reihenfolge des Loaders (lessonBookingPlanner.loader.ts, applies).
const keepReason = ({data, intent, isWaitlist}: SummaryInput) => {
    if (data.canceled) return "Buchung storniert";
    if (isWaitlist) return "Warteliste, der Preis entsteht beim Nachrücken";
    // Reihenfolge wie im Planner: 0 € vor Pauschalpreis.
    if (data.priceBefore < 0.005) return "Buchung zu 0 €";
    if (data.unitPrice === null || data.unitPrice === undefined) return "Pauschalpreis";
    if (data.pricingMode === LessonPricingMode.Individual && !intent?.chargeUnitChanges) return "individueller Preis";
    if (data.regenerated) return "Zuordnung der Einheiten offen";
    if (data.applies !== LessonBookingPlanApplies.Money) return "Preis und Raten stimmen schon vorher nicht überein";

    return undefined;
};

export const buildSummaryRows = (input: SummaryInput): Array<SummaryRow> => {
    const {data} = input;
    const rows: Array<SummaryRow> = [unitsRow(data)];
    const money = data.applies === LessonBookingPlanApplies.Money;
    const priceDelta = data.priceAfter - data.priceBefore;
    const priceChanged = money && changed(data.priceAfter, data.priceBefore);
    // Eine neu geplante Rate, die das Büro gelöscht hat, entsteht nie: weder Änderung noch übrige Rate (wie isDroppedNewRate im Executor).
    const rates = data.rates.filter((rate) => !(rate.status === LessonBookingPlannerRateStatus.New && rate.deleted));
    const changedRates = money ? rates.filter((rate) => rate.status === LessonBookingPlannerRateStatus.New || rate.edited || changed(rate.amountAfter, rate.amountBefore)) : [];
    const credit = money && data.credit > 0.005;
    const bound = rates.filter((rate) => rate.status === LessonBookingPlannerRateStatus.Bound).length;
    const others = rates.length - changedRates.length;
    const othersText = joinDetail(
        others === 0 ? undefined : count(others, "die übrige Rate bleibt", `die übrigen ${others} Raten bleiben`),
        bound > 0 && others > 0 ? `${bound} davon gebunden` : undefined,
    );
    const ratesAlso = rates.length > 0 && changedRates.length === 0 && !credit ? " und Raten" : "";

    rows.push(
        priceChanged
            ? {key: "price", number: signed(priceDelta), text: `Preis, jetzt ${eur(data.priceAfter)}`, detail: `vorher ${eur(data.priceBefore)}`, tone: tone(priceDelta)}
            : {key: "price", number: "bleibt", text: `Preis ${eur(data.priceBefore)}${ratesAlso}`, detail: keepReason(input), tone: "keep"},
    );
    if (changedRates.length > MAX_RATE_ROWS) {
        const sum = changedRates.reduce((total, rate) => total + rate.amountAfter - rate.amountBefore, 0);

        rows.push({
            key: "rates",
            number: signed(sum),
            text: `${changedRates.length} Raten geändert`,
            detail: joinDetail(changedRates.map((rate) => day(rate.paymentDue)).join(", "), othersText),
            tone: tone(sum),
        });
    } else {
        changedRates.forEach((rate, index) => {
            const delta = rate.amountAfter - rate.amountBefore;
            const last = index === changedRates.length - 1 ? othersText : undefined;

            rows.push(
                rate.status === LessonBookingPlannerRateStatus.New
                    ? {key: `rate-${index}`, number: signed(delta), text: `Neue Rate ${date(rate.paymentDue)}`, detail: last, tone: tone(delta)}
                    : rate.deleted
                    ? {key: `rate-${index}`, number: signed(delta), text: `Rate ${date(rate.paymentDue)} gelöscht`, detail: joinDetail("von Hand", last), tone: tone(delta)}
                    : {
                          key: `rate-${index}`,
                          number: signed(delta),
                          text: `Rate ${date(rate.paymentDue)}, jetzt ${eur(rate.amountAfter)}`,
                          detail: joinDetail(rate.paymentDueBefore ? `vorher ${eur(rate.amountBefore)}, fällig ${date(rate.paymentDueBefore)}` : `vorher ${eur(rate.amountBefore)}`, rate.edited ? "von Hand" : undefined, last),
                          tone: tone(delta),
                      },
            );
        });
    }
    if (credit) {
        // Ohne geänderte Rate erklärt erst das Guthaben, warum keine Rate sinkt: alle sind gebunden.
        const why =
            changedRates.length === 0 && bound > 0
                ? count(bound, "die einzige Rate ist gebunden und kann den Ausgleich nicht tragen", `alle ${bound} Raten gebunden, keine kann den Ausgleich tragen`)
                : "keine offene Rate kann den Ausgleich tragen";

        rows.push({key: "credit", number: eur(data.credit), text: "Guthaben für den Bucher", detail: why, tone: "credit"});
    }
    // Die Hinweise selbst stehen unter der Vorschau bei den Entscheidungen; hier nur, dass es welche gibt.
    if (!data.canceled && data.warnings.length > 0) {
        rows.push({
            key: "warnings",
            number: String(data.warnings.length),
            text: count(data.warnings.length, "Hinweis für das Büro", "Hinweise für das Büro"),
            detail: "unter der Vorschau",
            tone: "open",
        });
    }

    return rows;
};

// Farbe des Kastens (Variante A, 27.10.2026): Guthaben gold, Geld bewegt sich blau, sonst grün (alles bleibt).
export type SummaryBoxTone = "credit" | "money" | "keep";
export const summaryBoxTone = (rows: Array<SummaryRow>): SummaryBoxTone => {
    if (rows.some((row) => row.tone === "credit")) return "credit";
    if (rows.some((row) => row.key !== "units" && row.key !== "warnings" && (row.tone === "minus" || row.tone === "plus" || row.tone === "neutral"))) return "money";

    return "keep";
};

const boxClass: Record<SummaryBoxTone, string> = {credit: css.boxCredit, money: css.boxMoney, keep: css.boxKeep};

const PlannerSummary: FC<SummaryInput> = (input) => {
    const rows = buildSummaryRows(input);

    return (
        <div className={classNames(css.summary, boxClass[summaryBoxTone(rows)])} data-testid="planner-summary">
            {rows.map((row) => (
            <div key={row.key} className={classNames(css.row, css[row.tone])}>
                <span className={css.dot} />
                <span className={css.number}>{row.number}</span>
                <span className={css.text}>
                    {row.text}
                    {row.detail && <small className={css.detail}>{row.detail}</small>}
                </span>
                </div>
            ))}
        </div>
    );
};

export default PlannerSummary;
